/**
 * useKaneAssistant
 *
 * Abre Kane_OneDeck (el agente) en un popup lateral con handoff SSO.
 *
 * Vivia inline en PrivateLayout.js como la unica forma de llegar al agente.
 * Se extrae aca porque hay un segundo punto de entrada legitimo — el aviso
 * "Asistente AI Disponible" del AdminDashboard, que hasta #174 mandaba a un
 * boton de chat que ya no existe — y duplicar el flujo de token seria tener
 * dos verdades para la misma puerta.
 *
 * @module hooks/useKaneAssistant
 */

import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getKaneHandoffToken } from '../services/kaneAuth';
import { toast } from '../utils/toast';

/**
 * @returns {Function} openKaneAssistant - abre el popup del agente
 */
export const useKaneAssistant = () => {
  const { user, profile } = useAuth();

  // Issue #174: kaneAuth.getKaneHandoffToken tries the silent refresh path
  // first (when a persistent link exists in oauth_identities), falling back to
  // the short-lived JWT handoff that mints a fresh link via Kane's
  // /auth/sso-prelink. Popup is opened synchronously (popup-blocker friendly)
  // and navigated once the token is in hand. If both refresh and handoff fail,
  // we fall back to legacy URL params so audit logging still works.
  return React.useCallback(async () => {
    const kaneFallback = process.env.REACT_APP_KANE_URL || 'https://kane-onedeck.vercel.app';
    const fallbackParams = new URLSearchParams({
      agent: 'kane-promedic',
      requesterEmail: user?.email || '',
      requesterName: profile?.full_name || '',
    });
    const win = window.open(
      'about:blank',
      'kane-promedic-assistant',
      'width=480,height=820,menubar=no,toolbar=no,location=no,status=no'
    );
    if (!win) {
      // eslint-disable-next-line no-console
      console.warn('Kane Assistant popup was blocked by the browser.');
      toast.error('El navegador bloqueó la ventana emergente del Asistente Kane. Permite ventanas emergentes para este sitio e intenta de nuevo.');
      return;
    }

    try {
      const { token, kaneUrl } = await getKaneHandoffToken();
      const targetUrl = kaneUrl || kaneFallback;
      win.location.replace(`${targetUrl}/sso#token=${encodeURIComponent(token)}`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Kane SSO handoff failed; opening with legacy params:', err);
      win.location.replace(`${kaneFallback}/?${fallbackParams.toString()}`);
    }
  }, [user, profile]);
};

export default useKaneAssistant;
